import * as request from 'superagent'
import qaLogger from 'lib/QANetworkLogger'
import { get, isObject } from 'lodash'
import { IDispatch } from 'common/interfaces.d'
import { getToken } from 'common/selectors/accessToken'
import { fetchUserToken } from 'sideEffects/UserToken/actions'
import { requestCacheMiddleware } from 'lib/Cache'
import RouterHelper, { IBackendRouter } from 'lib/BackendRouter'
import {
  HTTP_UNAUTHORIZED,
  AJAX_REQUEST_HEADERS,
  HEADER_ACCESS_TOKEN_FIELD,
  HTTP_OK
} from 'common/constants'
import { LOGOUT } from 'common/apiRoutes'

interface IWindow extends Window {
  dataLayer: Array<unknown>
}

declare var window: IWindow

const Router: IBackendRouter = RouterHelper()
const qaLoggerWrapper = qaLogger.init(window).getRequestWrapper()

let dispacher: IDispatch | undefined
export let getStore

const requestGroups: { [index: string]: Array<request.Request> } = {}

const redirectToLogout = function() {
  window.location.assign(Router.generate(LOGOUT))
}

const unauthorizedRedirect = function(req: request.SuperAgentRequest) {
  req.on('response', function(res) {
    if (res.status === HTTP_UNAUTHORIZED) {
      window.location.assign('/')
    }
  })
}

export function addToGroup(
  request: request.SuperAgentRequest,
  groupName: string
): request.SuperAgentRequest {
  request.on(
    'request',
    (req: request.Request): void => {
      if (typeof requestGroups[groupName] === 'undefined') {
        requestGroups[groupName] = []
      }

      requestGroups[groupName].push(req)
    }
  )

  return request
}

export function abortRequestsInGroup(groupName: string): void {
  if (Array.isArray(requestGroups[groupName]) === true) {
    requestGroups[groupName].forEach(
      (request: request.Request): void => {
        request.abort()
      }
    )

    delete requestGroups[groupName]
  }
}

const supportForRedirect = function(req: request.SuperAgentRequest) {
  req.on('response', function(res) {
    const redirect = get(res, 'body.extras.redirect')

    if (typeof redirect === 'string' && redirect !== '') {
      window.location.assign(redirect)
    }
  })
}

const supportForDataLayer = function(req: request.SuperAgentRequest) {
  req.on('response', function(res) {
    const gtm = get(res, 'body.extras.gtm', undefined)

    if (!window.dataLayer) {
      window.dataLayer = []
    }

    if (Array.isArray(gtm) === true) {
      gtm.forEach(function(dataEvent) {
        window.dataLayer.push(dataEvent)
      })
    } else if (isObject(gtm) === true) {
      window.dataLayer.push(gtm)
    }
  })
}

const obtainUserToken = callback => {
  fetchUserToken(getStore(), dispacher, authorizedGet, callback)
}

const addAccessTokenToHeader = (
  req: request.SuperAgentRequest
): request.SuperAgentRequest => {
  if (typeof getStore === 'function') {
    req.set(HEADER_ACCESS_TOKEN_FIELD, `Bearer ${getToken(getStore())}`)
  } else {
    console.warn(
      'Can not add AccessToken to the request header (store not provided)'
    )
  }

  return req
}

type SuperAgentRequestCB = request.SuperAgentRequest & {
  _callbacks
}

export const addUserTokenSupport = function(
  req: request.SuperAgentRequest
): request.SuperAgentRequest {
  const dummyObject = {}

  if (typeof Proxy === 'undefined' || !Proxy) {
    return req
  }
  const proxy = new Proxy(dummyObject, {
    get(_target, name) {
      if (name === 'then') {
        return function(resolve, reject) {
          const fullfilledPromise = new Promise((innerResolve, innerReject) => {
            req.end(function(err, res) {
              if (err) {
                if (res && res.status === HTTP_UNAUTHORIZED) {
                  obtainUserToken(() => {
                    const newRequest = request(req.url) as SuperAgentRequestCB

                    const toCopy = [
                      'header',
                      'method',
                      '_query',
                      '_data',
                      '_header'
                    ]

                    toCopy.forEach(methodName => {
                      newRequest[methodName] = req[methodName]
                    })
                    newRequest._callbacks = {
                      $response: get(
                        (req as SuperAgentRequestCB)._callbacks,
                        '$response',
                        []
                      )
                    }

                    addAccessTokenToHeader(newRequest)

                    newRequest.end((newErr, newRes) => {
                      if (newErr !== null) {
                        if (newRes && newRes.status === HTTP_UNAUTHORIZED) {
                          return redirectToLogout()
                        }
                        innerReject(newErr)
                      } else {
                        innerResolve(newRes)
                      }
                    })
                  })
                  return
                }

                innerReject(err)
              } else {
                innerResolve(res)
              }
            })
          })

          return fullfilledPromise.then(resolve, reject)
        }
      }

      return function() {
        req[name].apply(req, Array.prototype.slice.call(arguments))

        return proxy
      }
    }
  })

  return proxy as request.SuperAgentRequest
}

export function isSuccessfulResponse(response: request.Response): boolean {
  return (
    response &&
    response.ok &&
    response.status === HTTP_OK &&
    response.body &&
    response.body.success
  )
}

export function authorizedJavaGet(url: string) {
  return addUserTokenSupport(
    addAccessTokenToHeader(
      request
        .get(url)
        .use(qaLoggerWrapper)
        .set('Accept', 'application/json')
        .set('Access-Control-Max-Age', '-1')
        .set(AJAX_REQUEST_HEADERS)
    )
  )
}

export function authorizedJavaPost(url: string) {
  return addUserTokenSupport(
    addAccessTokenToHeader(
      request
        .post(url)
        .use(qaLoggerWrapper)
        .set('Accept', 'application/json')

        .set(AJAX_REQUEST_HEADERS)
    )
  )
}

export function authorizedJavaPut(url: string): request.SuperAgentRequest {
  return addUserTokenSupport(
    addAccessTokenToHeader(
      request
        .put(url)
        .use(qaLoggerWrapper)
        .set('Accept', 'application/json')
        .set(AJAX_REQUEST_HEADERS)
    )
  )
}

export function authorizedJavaDelete(url: string) {
  return addUserTokenSupport(
    addAccessTokenToHeader(
      request
        .delete(url)
        .use(qaLoggerWrapper)
        .set('Accept', 'application/json')
        .set(AJAX_REQUEST_HEADERS)
    )
  )
}

export function authorizedJavaPatch(url: string) {
  return addUserTokenSupport(
    addAccessTokenToHeader(
      request
        .patch(url)
        .use(qaLoggerWrapper)
        .set(AJAX_REQUEST_HEADERS)
        .set('Accept', 'application/json-patch+json')
        .set('Content-Type', 'application/json-patch+json')
    )
  )
}

export function authorizedGet(url: string): request.SuperAgentRequest {
  return request
    .get(url)
    .set(AJAX_REQUEST_HEADERS) // make it ajax call
    .use(qaLoggerWrapper)
    .use(unauthorizedRedirect)
    .use(supportForDataLayer)
    .use(supportForRedirect)
}

interface IAuthorizedGetCached {
  url: string
  forceUpdate: boolean
  doQuery: boolean
}

export function authorizedGetCached({
  url,
  forceUpdate = false,
  doQuery = true
}: IAuthorizedGetCached) {
  return (authorizedGet(url) as any)
    .use(requestCacheMiddleware)
    .forceUpdate(forceUpdate)
    .doQuery(doQuery) as request.SuperAgentRequest
}

export function authorizedGetFromCache(url: string) {
  return authorizedGetCached({ url, forceUpdate: false, doQuery: false })
}

export function authorizedGetForceCacheUpdate(url: string) {
  return authorizedGetCached({ url, forceUpdate: true, doQuery: true })
}

export function authorizedPost(url: string) {
  return request
    .post(url)
    .set(AJAX_REQUEST_HEADERS) // make it ajax call
    .use(qaLoggerWrapper)
    .use(unauthorizedRedirect)
    .use(supportForDataLayer)
    .use(supportForRedirect)
}

export function authorizedPostUpload(url: string, files) {
  let req = request
    .post(url)
    .set(AJAX_REQUEST_HEADERS) // make it ajax call
    .use(supportForDataLayer)
    .use(unauthorizedRedirect)
  files.forEach(file => {
    req.attach('file', file)
  })
  return req
}

export const checkSuccess = res => {
  if (res.body.success === false) {
    throw new Error('Request failed.')
  }

  return res
}

export function setDispacher(dispacherRef: IDispatch): void {
  dispacher = dispacherRef
}

export function setGetStore(getStoreRef): void {
  getStore = getStoreRef
}
